import React from 'react'
import moment from 'moment'
import { Grid, Segment, SemanticICONS } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { listSubset } from 'app-support/stats/state/requests'
import { DetailSegment } from './DetailSegment'
import { YearSelector } from './YearSelector'
import { YearSwitcher } from './YearSwitcher'
import { apiFormat, format } from '@cbgms/base/utils/datetime'
import { endOfYear, endOfYearFn, firstYearFn, lastYearFn, startOfYear, startOfYearFn } from '../utils'
import { useNotifier } from '@cbgms/base/notifier'
import { Redirect } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import isEmpty from 'lodash/isEmpty'
import { IRootReducerState } from 'state/reducers'
import StatsLayout from './StatsLayout'
import { Subset } from '@cbgms/api'
import GenericStatsYearChart from './GenericStatsYearChart'
import GenericStatsMonthChart from './GenericStatsMonthChart'

interface GenericStatsDetailsProps {
  entity: Subset
  entityTranslation: string
  icon: SemanticICONS
}

export const GenericStatsDetails: React.FC<GenericStatsDetailsProps> = ({ entity, entityTranslation, icon }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()

  const [selectedYear, setSelectedYear] = React.useState(moment().year().toString())
  const formattedStartOfYear = startOfYearFn(selectedYear)
  const formattedEndOfYear = endOfYearFn(selectedYear)

  const [yearOffset, setYearOffset] = React.useState(0)
  const firstYear = firstYearFn(yearOffset)
  const lastYear = lastYearFn(yearOffset)

  const { country, shopModules, companyUUID, shopID } = useSelector((state: IRootReducerState) => state.stats)

  const dataResult = useQuery(
    ['Statistics', entity, { selectedYear, yearOffset, country, shopModules, companyUUID, shopID }],
    async () => {
      const [monthResult, yearResult, graphResult, totalResult] = await Promise.all([
        dispatch(
          listSubset({
            CompanyUUID: companyUUID,
            ShopID: shopID,
            ShopModules: shopModules,
            EndDate: endOfYear,
            StartDate: startOfYear,
            Subset: entity,
            TimeFrame: 'month',
            Country: country
          })
        ),
        dispatch(
          listSubset({
            CompanyUUID: companyUUID,
            ShopID: shopID,
            ShopModules: shopModules,
            EndDate: lastYear,
            StartDate: firstYear,
            Subset: entity,
            TimeFrame: 'year',
            Country: country
          })
        ),
        dispatch(
          listSubset({
            CompanyUUID: companyUUID,
            ShopID: shopID,
            ShopModules: shopModules,
            EndDate: formattedEndOfYear,
            StartDate: formattedStartOfYear,
            Subset: entity,
            TimeFrame: 'month',
            Country: country
          })
        ),
        dispatch(
          listSubset({
            CompanyUUID: companyUUID,
            ShopID: shopID,
            ShopModules: shopModules,
            EndDate: endOfYear,
            StartDate: firstYear,
            Subset: entity,
            TimeFrame: 'year',
            Country: country
          })
        )
      ])

      return {
        monthly: monthResult.Data,
        yearly: yearResult.Data,
        graph: graphResult.Data,
        total: totalResult.Data
      }
    },
    { refetchOnWindowFocus: false, refetchOnMount: false }
  )

  if (dataResult.isFetched && isEmpty(dataResult.data)) {
    notifier.error(t('global.something_went_wrong'))

    return <Redirect to='/stats' />
  }

  const totalData =
    dataResult.data?.total.reduce((totalValue, item) => {
      return totalValue + item.Total
    }, 0) || 0

  const currentMonthData = dataResult.data?.monthly.find(
    item => item.StartDate === moment().startOf('month').format(apiFormat.yearMonthDay)
  )

  const lastMonthData = dataResult.data?.monthly.find(
    item => item.StartDate === moment().startOf('month').subtract(1, 'month').format(apiFormat.yearMonthDay)
  )

  return (
    <StatsLayout testId='segment-stats' title={entityTranslation} showDateFilters={false}>
      <Grid.Row columns='equal'>
        <Grid.Column>
          <DetailSegment title={t('global.stats.total', { entityName: entityTranslation })} main_icon={icon} value={totalData} />
        </Grid.Column>
        <Grid.Column>
          <DetailSegment
            title={`${t('global.stats.current_month')}: ${moment().format(format.monthLong)}`}
            main_icon={icon}
            additional_icon='arrow down'
            value={(currentMonthData && currentMonthData.Total) || 0}
          />
        </Grid.Column>
        <Grid.Column>
          <DetailSegment
            title={`${t('global.stats.last_month')}: ${moment().subtract(1, 'month').format(format.monthLong)}`}
            main_icon={icon}
            additional_icon='arrow left'
            value={(lastMonthData && lastMonthData.Total) || 0}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns='equal'>
        <Grid.Column>
          <Segment className='segment-chart'>
            <YearSwitcher
              title={t('global.stats.per_year', { entityName: entityTranslation })}
              setYear={setYearOffset}
              yearOffset={yearOffset}
              loading={dataResult.isLoading}
            />
            <br />
            <br />
            <GenericStatsYearChart data={dataResult.data?.yearly || []} entityTranslation={entityTranslation} />
          </Segment>
        </Grid.Column>
        <Grid.Column>
          <Segment className='segment-chart'>
            <YearSelector
              title={t('global.stats.per_month', { entityName: entityTranslation })}
              placeholder={selectedYear.toString()}
              onYearChange={setSelectedYear}
              loading={dataResult.isLoading}
            />
            <br />
            <GenericStatsMonthChart data={dataResult.data?.graph || []} entityTranslation={entityTranslation} />
          </Segment>
        </Grid.Column>
      </Grid.Row>
    </StatsLayout>
  )
}

export default GenericStatsDetails
